import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import OccasionReviewed from './OccasionReviewed';

/**
 * Occasion Review — set Primary Occasion one design at a time.
 *
 * The catalog has ~1,450 designs and the field is unreliable across most of
 * them, so this page optimises for throughput: one big image, chips bound to
 * number keys, Enter to save and advance. Everything else is deliberately
 * absent.
 */

const API_BASE = process.env.REACT_APP_API_URL || '';

// Mirrors the palette in App.js. Duplicated rather than imported because
// App.js imports this file — importing back would create a cycle.
const C = {
  maroon: '#8B1D3C',
  gold: '#D4AF37',
  bg: '#FFFFFF',
  bgLight: '#F9FAFB',
  textPrimary: '#111827',
  textSecondary: '#6B7280',
  textLight: '#9CA3AF',
  border: '#E5E7EB',
  verified: '#10B981',
};

const PER_PAGE = 50;
// Fetch more once the reviewer is this close to the end of what is loaded.
const TOP_UP_MARGIN = 5;

const authHeaders = () => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const imageSrc = (url) => {
  if (!url) return '';
  return /^https?:\/\//i.test(url) ? url : `${API_BASE}${url}`;
};

export default function OccasionReview() {
  const [values, setValues] = useState([]);
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(0);
  const [picks, setPicks] = useState({});      // designNumber -> string[]
  const [remaining, setRemaining] = useState(null);
  const [skippedCount, setSkippedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exhausted, setExhausted] = useState(false);
  const [error, setError] = useState('');
  const [showRecords, setShowRecords] = useState(false);

  // Design numbers saved in this sitting. A Set, so pressing Back and saving
  // the same design again counts once — the previous counter counted clicks
  // and drifted above the true number.
  const savedRef = useRef(new Set());
  const [savedCount, setSavedCount] = useState(0);

  // Guards double submission. State updates are async, so two fast Enters can
  // both pass a state-based check before either takes effect.
  const inFlight = useRef(false);
  const fetching = useRef(false);

  const current = queue[index] || null;
  const selected = useMemo(
    () => (current ? picks[current.designNumber] || [] : []),
    [current, picks],
  );

  /**
   * Fetch a batch. `after` is a design number cursor; omit it to start over.
   * Paginating by cursor rather than page number means saves (which remove
   * rows from the queue) and skips (which do not) can never shift or hide a
   * batch the way an offset does.
   */
  const fetchBatch = useCallback(async ({ after = null, replace = false } = {}) => {
    if (fetching.current) return;
    fetching.current = true;
    try {
      const url = `${API_BASE}/api/occasion-review/queue?per_page=${PER_PAGE}`
        + (after ? `&after=${encodeURIComponent(after)}` : '');
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Could not load the queue (${res.status})`);
      const data = await res.json();

      setValues(data.values || []);
      setRemaining(data.remaining ?? 0);

      const batch = data.designs || [];
      setPicks((prev) => {
        const next = { ...prev };
        batch.forEach((d) => {
          if (next[d.designNumber] === undefined) next[d.designNumber] = d.occasions || [];
        });
        return next;
      });

      if (replace) {
        setQueue(batch);
        setIndex(0);
        setExhausted(batch.length === 0);
      } else {
        setQueue((prev) => {
          const seen = new Set(prev.map((d) => d.designNumber));
          return [...prev, ...batch.filter((d) => !seen.has(d.designNumber))];
        });
        if (batch.length === 0) setExhausted(true);
      }
      setError('');
    } catch (e) {
      setError(e.message || 'Could not load the queue');
    } finally {
      fetching.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBatch({ replace: true }); }, [fetchBatch]);

  const toggle = useCallback((value) => {
    if (!current) return;
    setPicks((prev) => {
      const dn = current.designNumber;
      const currentPicks = prev[dn] || [];
      return {
        ...prev,
        [dn]: currentPicks.includes(value)
          ? currentPicks.filter((v) => v !== value)
          : [...currentPicks, value],
      };
    });
  }, [current]);

  const advance = useCallback(() => {
    setIndex((prev) => prev + 1);
  }, []);

  // Top up from the end of what is loaded, never from the front. Kept in an
  // effect rather than inside a state updater so it cannot fire twice.
  useEffect(() => {
    if (loading || exhausted || queue.length === 0) return;
    if (index < queue.length - TOP_UP_MARGIN) return;
    fetchBatch({ after: queue[queue.length - 1].designNumber });
  }, [index, queue, loading, exhausted, fetchBatch]);

  const save = useCallback(async () => {
    if (!current || inFlight.current) return;
    inFlight.current = true;
    setSaving(true);
    const dn = current.designNumber;
    try {
      const res = await fetch(
        `${API_BASE}/api/occasion-review/${encodeURIComponent(dn)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ occasions: selected }),
        },
      );
      const data = await res.json();
      if (!res.ok || data.success === false) {
        throw new Error(data.error || `Save failed (${res.status})`);
      }
      setRemaining(data.remaining ?? remaining);
      savedRef.current.add(dn);
      setSavedCount(savedRef.current.size);
      setError('');
      advance();
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  }, [current, selected, remaining, advance]);

  // Skip records nothing, so the design simply stays in the queue and comes
  // back next time the page is opened.
  const skip = useCallback(() => {
    if (!current) return;
    setSkippedCount((n) => n + 1);
    advance();
  }, [current, advance]);

  const back = useCallback(() => setIndex((prev) => Math.max(0, prev - 1)), []);

  const restart = useCallback(() => {
    savedRef.current = new Set();
    setSavedCount(0);
    setSkippedCount(0);
    setExhausted(false);
    setLoading(true);
    fetchBatch({ replace: true });
  }, [fetchBatch]);

  // Number keys pick, Enter saves. Arrow keys are deliberately NOT bound:
  // an accidental arrow press used to skip a design silently.
  useEffect(() => {
    const onKey = (e) => {
      // The records view has its own inputs and no design on screen; leaving
      // the shortcuts live there would save the design behind it.
      if (showRecords) return;
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key >= '1' && e.key <= '9') {
        const value = values[Number(e.key) - 1];
        if (value) { e.preventDefault(); toggle(value); }
      } else if (e.key === 'Enter') {
        // Stops a focused Save button from also firing its own click.
        e.preventDefault();
        save();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [values, toggle, save, showRecords]);

  if (showRecords) {
    return <OccasionReviewed onBack={() => setShowRecords(false)} />;
  }

  if (loading) return <div style={S.centre}>Loading designs…</div>;

  if (!current) {
    return (
      <div style={S.centre}>
        <div style={{ fontSize: 44, marginBottom: 12, color: C.verified }}>✓</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: C.textPrimary }}>
          {remaining ? 'Reached the end of the queue' : 'Queue cleared'}
        </div>
        <div style={{ color: C.textSecondary, marginTop: 6 }}>
          {savedCount} design{savedCount === 1 ? '' : 's'} saved this session
          {skippedCount > 0 && `, ${skippedCount} skipped`}.
          {remaining > 0 && ` ${remaining} still unreviewed — start again to pick them up.`}
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button style={S.secondaryBtn} onClick={restart}>
            Start from the beginning
          </button>
          <button style={S.secondaryBtn} onClick={() => setShowRecords(true)}>
            View records
          </button>
        </div>
      </div>
    );
  }

  const retired = current.retiredOccasions || [];

  return (
    <div style={S.page}>
      <header style={S.header}>
        <div>
          <h1 style={S.title}>Occasion Review</h1>
          <p style={S.subtitle}>
            Set <strong style={{ color: C.textPrimary }}>Primary Occasion</strong> —
            press <kbd style={S.kbd}>1</kbd>–<kbd style={S.kbd}>{Math.min(values.length, 9)}</kbd> to
            pick, <kbd style={S.kbd}>Enter</kbd> to save
          </p>
        </div>
        <div style={{ display: 'flex', gap: 18, alignItems: 'flex-end' }}>
          <button onClick={() => setShowRecords(true)} style={S.recordsBtn}>
            View records →
          </button>
          <div style={S.counter}>
            <div style={S.counterNumber}>{remaining ?? '—'}</div>
            <div style={S.counterLabel}>left to review</div>
          </div>
        </div>
      </header>

      {error && <div style={S.error}>{error}</div>}

      <div style={S.card}>
        <div style={S.imageWrap}>
          {current.imageUrl ? (
            <img src={imageSrc(current.imageUrl)} alt={current.designNumber}
                 style={S.image} />
          ) : (
            <div style={S.noImage}>No image</div>
          )}
        </div>

        <div style={S.panel}>
          <div>
            <div style={S.designNumber}>{current.designNumber}</div>
            <div style={S.itemName}>
              {current.itemName || current.productType || 'Design'}
            </div>
            {current.brand && <div style={S.brand}>{current.brand}</div>}
          </div>

          {current.isAiGuess && current.occasions.length > 0 && (
            <div style={S.aiBadge}>
              <span style={{ fontWeight: 700 }}>AI guess</span>
              {' — '}{current.occasions.join(', ')}. Confirm or correct.
            </div>
          )}
          {retired.length > 0 && (
            <div style={S.retiredBadge}>
              Previously <strong>{retired.join(', ')}</strong> — no longer an
              option, so it has been dropped. Pick again below.
            </div>
          )}
          {!current.occasions.length && !retired.length && (
            <div style={S.blankBadge}>Nothing recorded yet</div>
          )}

          <div style={S.chips}>
            {values.map((value, i) => {
              const on = selected.includes(value);
              return (
                <button key={value} onClick={() => toggle(value)}
                        style={{ ...S.chip, ...(on ? S.chipOn : null) }}
                        aria-pressed={on}>
                  <span style={{ ...S.chipKey, ...(on ? S.chipKeyOn : null) }}>
                    {i + 1}
                  </span>
                  {value}
                </button>
              );
            })}
          </div>

          <div style={S.actions}>
            <button onClick={back} disabled={index === 0}
                    style={{ ...S.secondaryBtn, opacity: index === 0 ? 0.4 : 1 }}>
              ← Back
            </button>
            <button onClick={skip} style={S.secondaryBtn}>Skip →</button>
            <button onClick={save} disabled={saving} style={S.saveBtn}>
              {saving ? 'Saving…' : 'Save & Next →'}
            </button>
          </div>

          <div style={S.progress}>
            {savedCount} saved this session
            {skippedCount > 0 && ` · ${skippedCount} skipped`}
          </div>
        </div>
      </div>
    </div>
  );
}

const S = {
  page: { padding: '28px 32px', maxWidth: 1180, margin: '0 auto' },
  centre: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', minHeight: '60vh', color: C.textSecondary,
    textAlign: 'center',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
    marginBottom: 22, paddingBottom: 18, borderBottom: `1px solid ${C.border}`,
    gap: 24, flexWrap: 'wrap',
  },
  title: {
    fontSize: 30, fontWeight: 800, color: C.textPrimary, margin: 0,
    letterSpacing: '-0.02em',
  },
  subtitle: { fontSize: 14, color: C.textSecondary, margin: '6px 0 0' },
  kbd: {
    background: C.bgLight, border: `1px solid ${C.border}`, borderRadius: 4,
    padding: '1px 6px', fontSize: 12, fontFamily: 'ui-monospace, monospace',
    color: C.textPrimary,
  },
  recordsBtn: {
    padding: '9px 16px', borderRadius: 8, cursor: 'pointer',
    border: `1px solid ${C.border}`, background: C.bg, color: C.textSecondary,
    fontSize: 13.5, fontWeight: 600, marginBottom: 4,
  },
  counter: { textAlign: 'right', lineHeight: 1 },
  counterNumber: {
    fontSize: 38, fontWeight: 800, color: C.maroon, letterSpacing: '-0.03em',
    fontVariantNumeric: 'tabular-nums',
  },
  counterLabel: {
    fontSize: 11, color: C.textLight, textTransform: 'uppercase',
    letterSpacing: '0.09em', marginTop: 5,
  },
  error: {
    background: '#FEE2E2', color: '#991B1B', border: '1px solid #FCA5A5',
    borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 14,
  },
  card: {
    display: 'grid', gridTemplateColumns: 'minmax(260px, 440px) 1fr', gap: 32,
    background: C.bg, border: `1px solid ${C.border}`, borderRadius: 16,
    padding: 24, boxShadow: '0 1px 2px rgba(17,24,39,0.04), 0 12px 32px rgba(17,24,39,0.06)',
    alignItems: 'start',
  },
  imageWrap: {
    background: C.bgLight, borderRadius: 12, overflow: 'hidden',
    aspectRatio: '1 / 1', display: 'flex', alignItems: 'center',
    justifyContent: 'center', border: `1px solid ${C.border}`,
  },
  image: { width: '100%', height: '100%', objectFit: 'contain' },
  noImage: { color: C.textLight, fontSize: 14 },
  panel: { display: 'flex', flexDirection: 'column', gap: 20, minWidth: 0 },
  designNumber: {
    fontSize: 26, fontWeight: 800, color: C.textPrimary,
    letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums',
  },
  itemName: { fontSize: 15, color: C.textSecondary, marginTop: 3 },
  brand: {
    fontSize: 11, color: C.textLight, marginTop: 6,
    textTransform: 'uppercase', letterSpacing: '0.08em',
  },
  aiBadge: {
    background: '#FFFBEB', border: `1px solid ${C.gold}`, color: '#78350F',
    borderRadius: 8, padding: '9px 13px', fontSize: 13.5,
  },
  retiredBadge: {
    background: C.bgLight, border: `1px solid ${C.border}`, color: C.textSecondary,
    borderRadius: 8, padding: '9px 13px', fontSize: 13.5,
  },
  blankBadge: {
    background: C.bgLight, border: `1px dashed ${C.border}`, color: C.textLight,
    borderRadius: 8, padding: '9px 13px', fontSize: 13.5,
  },
  chips: { display: 'flex', flexWrap: 'wrap', gap: 10 },
  chip: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '11px 16px 11px 11px', borderRadius: 999, cursor: 'pointer',
    border: `1.5px solid ${C.border}`, background: C.bg, color: C.textPrimary,
    fontSize: 15, fontWeight: 600,
    transition: 'transform 120ms ease, border-color 120ms ease, background 120ms ease',
  },
  chipOn: {
    borderColor: C.maroon, background: 'rgba(139,29,60,0.06)', color: C.maroon,
    transform: 'translateY(-1px)',
  },
  chipKey: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 22, height: 22, borderRadius: 6, background: C.bgLight,
    color: C.textLight, fontSize: 11.5, fontWeight: 700,
    fontFamily: 'ui-monospace, monospace',
  },
  chipKeyOn: { background: C.maroon, color: '#fff' },
  actions: {
    display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
    paddingTop: 4,
  },
  secondaryBtn: {
    padding: '11px 18px', borderRadius: 9, cursor: 'pointer',
    border: `1px solid ${C.border}`, background: C.bg, color: C.textSecondary,
    fontSize: 14, fontWeight: 600,
  },
  saveBtn: {
    padding: '11px 26px', borderRadius: 9, cursor: 'pointer', border: 'none',
    background: C.maroon, color: '#fff', fontSize: 15, fontWeight: 700,
    marginLeft: 'auto', boxShadow: '0 2px 10px rgba(139,29,60,0.28)',
  },
  progress: {
    fontSize: 12.5, color: C.textLight, borderTop: `1px solid ${C.border}`,
    paddingTop: 14,
  },
};
